/**
 * Football Formations Data
 * Based on FA Youth Development Guidelines and Future Fit Team Sizes
 */

export interface Formation {
  id: string;
  name: string;
  notation: string;
  description: string;
  matchType: '5v5' | '7v7' | '9v9' | '11v11';
  ageGroup: string;
  positions: {
    goalkeeper: number;
    defenders: number;
    midfielders: number;
    forwards: number;
  };
}

export interface FormationCategory {
  matchType: '5v5' | '7v7' | '9v9' | '11v11';
  ageGroup: string;
  playerCount: number;
  formations: Formation[];
}

// FA Future Fit Recommended Team Sizes:
// U7-U8: 5v5
// U9-U10: 7v7
// U11-U12: 9v9
// U13+: 11v11

export const FORMATIONS: FormationCategory[] = [
  {
    matchType: '5v5',
    ageGroup: 'U7-U8',
    playerCount: 5,
    formations: [
      {
        id: '5v5-1-2-1',
        name: 'Balanced',
        notation: '1-2-1',
        description: 'Balanced formation with two defenders and one forward',
        matchType: '5v5',
        ageGroup: 'U7-U8',
        positions: { goalkeeper: 1, defenders: 2, midfielders: 1, forwards: 1 }
      },
      {
        id: '5v5-1-1-2',
        name: 'Attacking',
        notation: '1-1-2',
        description: 'Attacking formation with two forwards',
        matchType: '5v5',
        ageGroup: 'U7-U8',
        positions: { goalkeeper: 1, defenders: 1, midfielders: 1, forwards: 2 }
      },
      {
        id: '5v5-1-3-0',
        name: 'Defensive',
        notation: '1-3-0',
        description: 'Defensive formation with three defenders',
        matchType: '5v5',
        ageGroup: 'U7-U8',
        positions: { goalkeeper: 1, defenders: 3, midfielders: 0, forwards: 1 }
      },
      {
        id: '5v5-1-1-1-1',
        name: 'Diamond',
        notation: '1-1-1-1',
        description: 'Diamond formation with balanced coverage',
        matchType: '5v5',
        ageGroup: 'U7-U8',
        positions: { goalkeeper: 1, defenders: 1, midfielders: 2, forwards: 1 }
      }
    ]
  },
  {
    matchType: '7v7',
    ageGroup: 'U9-U10',
    playerCount: 7,
    formations: [
      {
        id: '7v7-1-2-3-1',
        name: 'Balanced midfield',
        notation: '1-2-3-1',
        description: 'Balanced formation with strong midfield presence',
        matchType: '7v7',
        ageGroup: 'U9-U10',
        positions: { goalkeeper: 1, defenders: 2, midfielders: 3, forwards: 1 }
      },
      {
        id: '7v7-1-3-2-1',
        name: 'Defensive',
        notation: '1-3-2-1',
        description: 'Defensive formation with three defenders',
        matchType: '7v7',
        ageGroup: 'U9-U10',
        positions: { goalkeeper: 1, defenders: 3, midfielders: 2, forwards: 1 }
      },
      {
        id: '7v7-1-2-2-2',
        name: 'Balanced',
        notation: '1-2-2-2',
        description: 'Balanced formation with two forwards',
        matchType: '7v7',
        ageGroup: 'U9-U10',
        positions: { goalkeeper: 1, defenders: 2, midfielders: 2, forwards: 2 }
      },
      {
        id: '7v7-1-3-1-2',
        name: 'Defensive with attack',
        notation: '1-3-1-2',
        description: 'Defensive with attacking options',
        matchType: '7v7',
        ageGroup: 'U9-U10',
        positions: { goalkeeper: 1, defenders: 3, midfielders: 1, forwards: 2 }
      },
      {
        id: '7v7-1-2-1-3',
        name: 'Attacking',
        notation: '1-2-1-3',
        description: 'Attacking formation with three forwards',
        matchType: '7v7',
        ageGroup: 'U9-U10',
        positions: { goalkeeper: 1, defenders: 2, midfielders: 1, forwards: 3 }
      },
      {
        id: '7v7-1-1-3-2',
        name: 'Midfield-heavy',
        notation: '1-1-3-2',
        description: 'Midfield-heavy attacking formation',
        matchType: '7v7',
        ageGroup: 'U9-U10',
        positions: { goalkeeper: 1, defenders: 1, midfielders: 3, forwards: 2 }
      }
    ]
  },
  {
    matchType: '9v9',
    ageGroup: 'U11-U14',
    playerCount: 9,
    formations: [
      {
        id: '9v9-1-3-3-2',
        name: 'Balanced with strong midfield',
        notation: '1-3-3-2',
        description: 'Balanced formation with strong midfield',
        matchType: '9v9',
        ageGroup: 'U11-U14',
        positions: { goalkeeper: 1, defenders: 3, midfielders: 3, forwards: 2 }
      },
      {
        id: '9v9-1-3-2-3',
        name: 'Attacking with three forwards',
        notation: '1-3-2-3',
        description: 'Attacking formation with three forwards',
        matchType: '9v9',
        ageGroup: 'U11-U14',
        positions: { goalkeeper: 1, defenders: 3, midfielders: 2, forwards: 3 }
      },
      {
        id: '9v9-1-4-3-1',
        name: 'Defensive with four defenders',
        notation: '1-4-3-1',
        description: 'Defensive formation with four defenders',
        matchType: '9v9',
        ageGroup: 'U11-U14',
        positions: { goalkeeper: 1, defenders: 4, midfielders: 3, forwards: 1 }
      },
      {
        id: '9v9-1-3-4-1',
        name: 'Midfield-dominant',
        notation: '1-3-4-1',
        description: 'Midfield-dominant formation',
        matchType: '9v9',
        ageGroup: 'U11-U14',
        positions: { goalkeeper: 1, defenders: 3, midfielders: 4, forwards: 1 }
      },
      {
        id: '9v9-1-2-4-2',
        name: 'Attacking midfield',
        notation: '1-2-4-2',
        description: 'Attacking midfield formation',
        matchType: '9v9',
        ageGroup: 'U11-U14',
        positions: { goalkeeper: 1, defenders: 2, midfielders: 4, forwards: 2 }
      },
      {
        id: '9v9-1-4-2-2',
        name: 'Solid defensive base',
        notation: '1-4-2-2',
        description: 'Solid defensive base with two forwards',
        matchType: '9v9',
        ageGroup: 'U11-U14',
        positions: { goalkeeper: 1, defenders: 4, midfielders: 2, forwards: 2 }
      }
    ]
  },
  {
    matchType: '11v11',
    ageGroup: 'U13+',
    playerCount: 11,
    formations: [
      {
        id: '11v11-1-4-4-2',
        name: 'Classic balanced',
        notation: '1-4-4-2',
        description: 'Classic balanced formation',
        matchType: '11v11',
        ageGroup: 'U13+',
        positions: { goalkeeper: 1, defenders: 4, midfielders: 4, forwards: 2 }
      },
      {
        id: '11v11-1-4-3-3',
        name: 'Attacking wide',
        notation: '1-4-3-3',
        description: 'Attacking formation with wide forwards',
        matchType: '11v11',
        ageGroup: 'U13+',
        positions: { goalkeeper: 1, defenders: 4, midfielders: 3, forwards: 3 }
      },
      {
        id: '11v11-1-4-2-3-1',
        name: 'Modern attacking',
        notation: '1-4-2-3-1',
        description: 'Modern formation with attacking midfielders',
        matchType: '11v11',
        ageGroup: 'U13+',
        positions: { goalkeeper: 1, defenders: 4, midfielders: 5, forwards: 1 }
      },
      {
        id: '11v11-1-3-5-2',
        name: 'Wing-back system',
        notation: '1-3-5-2',
        description: 'Wing-back system with midfield dominance',
        matchType: '11v11',
        ageGroup: 'U13+',
        positions: { goalkeeper: 1, defenders: 3, midfielders: 5, forwards: 2 }
      },
      {
        id: '11v11-1-4-5-1',
        name: 'Defensive',
        notation: '1-4-5-1',
        description: 'Defensive formation with lone striker',
        matchType: '11v11',
        ageGroup: 'U13+',
        positions: { goalkeeper: 1, defenders: 4, midfielders: 5, forwards: 1 }
      },
      {
        id: '11v11-1-3-4-3',
        name: 'Attacking 3-back',
        notation: '1-3-4-3',
        description: 'Attacking formation with three center-backs',
        matchType: '11v11',
        ageGroup: 'U13+',
        positions: { goalkeeper: 1, defenders: 3, midfielders: 4, forwards: 3 }
      },
      {
        id: '11v11-1-4-1-4-1',
        name: 'Defensive mid',
        notation: '1-4-1-4-1',
        description: 'Defensive midfielder protecting back four',
        matchType: '11v11',
        ageGroup: 'U13+',
        positions: { goalkeeper: 1, defenders: 4, midfielders: 5, forwards: 1 }
      },
      {
        id: '11v11-1-5-3-2',
        name: 'Very defensive',
        notation: '1-5-3-2',
        description: 'Very defensive formation with five defenders',
        matchType: '11v11',
        ageGroup: 'U13+',
        positions: { goalkeeper: 1, defenders: 5, midfielders: 3, forwards: 2 }
      }
    ]
  }
];

/**
 * Get formations for a specific age group
 * Uses the recommended match type based on FA Future Fit guidelines
 */
export const getFormationsByAgeGroup = (ageGroup: string): Formation[] => {
  const matchType = getRecommendedMatchType(ageGroup);
  return getFormationsByMatchType(matchType);
};

/**
 * Get formations for a specific match type
 */
export const getFormationsByMatchType = (matchType: '5v5' | '7v7' | '9v9' | '11v11'): Formation[] => {
  const category = FORMATIONS.find(cat => cat.matchType === matchType);
  return category?.formations || [];
};

/**
 * Get recommended match type based on age group
 * Based on FA Future Fit guidelines
 */
export const getRecommendedMatchType = (ageGroup: string): '5v5' | '7v7' | '9v9' | '11v11' => {
  // U7 and below, U8
  if (ageGroup.includes('U7') || ageGroup.includes('U8')) return '5v5';
  
  // U9, U10
  if (ageGroup.includes('U9') || ageGroup.includes('U10')) return '7v7';
  
  // U11, U12, U13, U14
  if (ageGroup.includes('U11') || ageGroup.includes('U12') || 
      ageGroup.includes('U13') || ageGroup.includes('U14')) return '9v9';
  
  // U15 and above
  return '11v11';
};

/**
 * Formation guidance for coaches
 */
export const FORMATION_GUIDANCE = {
  inPossession: {
    title: 'In-Possession Formation',
    description: 'The shape your team takes when you have the ball. This formation should support ball retention, progression, and creating goal-scoring opportunities.',
    tips: [
      'Consider formations with more midfielders for better ball circulation',
      'Wide formations (e.g., 1-4-3-3) provide width and stretching the opposition',
      'Central formations (e.g., 1-4-4-2) offer compactness and control'
    ]
  },
  outOfPossession: {
    title: 'Out-of-Possession Formation',
    description: 'The shape your team takes when the opposition has the ball. This formation should prioritize defensive organization, compactness, and winning the ball back.',
    tips: [
      'More defenders provide better defensive coverage',
      'Compact formations make it harder for opponents to play through',
      'Consider pressing intensity when choosing your shape'
    ]
  },
  secondary: {
    title: 'Secondary Formations',
    description: 'Alternative formations to use during the game based on match situations, opponent tactics, or game state (winning/losing).',
    tips: [
      'Have a more defensive option when protecting a lead',
      'Have a more attacking option when chasing a game',
      'Practice transitions between formations in training'
    ]
  }
};
